import {
  type DragRef,
  useDragDropContext,
} from '@wandb/weave/common/containers/DragDropContainer';
import {useCallback, useEffect, useMemo, useState} from 'react';

import {useMemoizedSelector} from '../../../state/hooks';
import {usePanelSearchDebouncedContext} from '../../../state/panelbank/PanelSearchContextProvider';
import * as ViewHooks from '../../../state/views/hooks';
import * as PanelBankSectionConfigActions from '../../../state/views/panelBankSectionConfig/actions';
import type * as PanelBankSectionConfigTypes from '../../../state/views/panelBankSectionConfig/types';
import {isPanelBankSection} from '../../../util/panelbank';
import {usePanelBankSectionsOpenTempContext} from '../contextProviders/PanelBankSectionsDuringSearch';
import {makeSectionIsOpenSelector} from '../selectors';

export function isSectionOpen(
  __id__: string,
  isOpen: boolean,
  isSearching: boolean,
  isSectionOpenWhileSearching: (
    sectionId: string,
    persistedValue: boolean
  ) => boolean,
  dragRef: DragRef | null,
  shiftKey: boolean
): boolean {
  const isOpenWhileSearching = isSectionOpenWhileSearching(__id__, isOpen);
  const isDraggingSection = dragRef != null && isPanelBankSection(dragRef);
  const forceCollapsed = isDraggingSection || shiftKey;

  // This is the final section open/close state (getter and setter) used in the UI
  const sectionIsOpen =
    !forceCollapsed && (isSearching ? isOpenWhileSearching : isOpen);
  return sectionIsOpen;
}

// This controls the open/close state of a single section in the panel bank.
// This state is derived from various sources, see inline comments.
export const usePanelBankSectionIsOpen = ({
  panelBankSectionConfigRef,
}: {
  panelBankSectionConfigRef: PanelBankSectionConfigTypes.Ref;
}) => {
  // isOpen and toggleIsOpen are for when no search is active
  const {__id__, isOpen} = useMemoizedSelector(
    makeSectionIsOpenSelector,
    panelBankSectionConfigRef
  );
  const toggleIsOpen = ViewHooks.useViewAction(
    panelBankSectionConfigRef,
    PanelBankSectionConfigActions.toggleIsOpen
  );

  // isOpenWhileSearching is a temporary override used during panel search
  const {isSearching} = usePanelSearchDebouncedContext();
  const {isSectionOpenWhileSearching, toggleIsOpenTemp} =
    usePanelBankSectionsOpenTempContext();
  const toggleIsOpenWhileSearching = useCallback(() => {
    toggleIsOpenTemp(__id__);
  }, [__id__, toggleIsOpenTemp]);

  // Another override!
  // If we're dragging a section, or dragging a panel while holding the shift key, collapse all sections temporarily.
  const {dragRef, shiftKey} = useDragDropContext();
  const sectionIsOpen = isSectionOpen(
    __id__,
    isOpen,
    isSearching,
    isSectionOpenWhileSearching,
    dragRef,
    shiftKey
  );

  const toggleSectionIsOpen = isSearching
    ? toggleIsOpenWhileSearching
    : toggleIsOpen;

  // One more thing...
  // We don't want to render collapsed sections until they're first opened,
  // but we also don't want to re-mount sections (and re-fetch panels) if you collapse and re-open them.
  // So we use this state variable, which flips to 'true' the first time the section is opened.
  const [shouldRenderSection, setShouldRenderSection] = useState(false);
  useEffect(() => {
    if (shouldRenderSection) {
      return;
    }
    if (sectionIsOpen) {
      setShouldRenderSection(sectionIsOpen);
    }
  }, [sectionIsOpen, shouldRenderSection]);

  return useMemo(
    () => ({
      sectionIsOpen,
      toggleSectionIsOpen,
      shouldRenderSection,
    }),
    [sectionIsOpen, shouldRenderSection, toggleSectionIsOpen]
  );
};
